import React, { useState } from "react";
import {
  Menu,
  BarChart,
  Users,
  Settings,
  LogOut,
  X,
  User,
  ImagePlay,
  MailCheck,
  Megaphone,
  UsersRound,
  Book,
  Library,
  Loader,
  Crown,
  UserCheck,
} from "lucide-react";
import { Scrollbars } from "react-custom-scrollbars-2"; // Import custom scrollbar
import styles from "./Dashboard.module.css";
import UsersContent from "../pages/content/UsersContent";
import SettingsAdmin from "../pages/content/SettingsAdmin";
import AdminProfile from "../pages/content/AdminProfile";
import BlogContent from "../pages/content/BlogContent";
import SuperadminSettings from "../pages/content/SuperadminSettings";
import useAuthStore from "../../store/authStore";
import DashboardContent from "./DashboardContent";
import { useAdminSettings } from "../../hooks/admin/useAdminSettings";
import MediaPage from "../pages/Media/MediaPage";
import NewsLetterPage from "../pages/newsletter/NewsLetterPage";
import BannerAds from "../pages/Banner-Ads/BannerAds";
import Loading from "../../common/HousGlass Loader/Loading";
import MyFollowing from "../pages/Following/MyFollowing";
import { useAdminProfile } from "../../hooks/admin/useAdminProfile";
import EbooksContent from "../pages/content/BooksContent";
import SeriesAdmin from "../pages/content/SeriesAdmin";
import SubscriptionAdmin from "../pages/content/SubscriptionAdmin";
import AuthorsDashboard from "../pages/Authors/AuthorsDashboard";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

function Dashboard() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [currentSection, setCurrentSection] = useState(
    location.state?.data || "dashboard"
  );
  const { user } = useAuthStore();
  const { settingsData, isLoading: isLoadingSettings } = useAdminSettings();
  const { profile } = useAdminProfile();
  const { signout, isLoading } = useAuth();

  if(!profile){
    console.log("profile is not loaded yet");
  }

  if (isLoadingSettings) {
    return <Loading />;
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSignOut = async () => {
    try {
      await signout();
      navigate("/");
    } catch (error) {
      console.error("Sign out failed:", error);
    }
  };

  const renderContent = () => {
    switch (currentSection) {
      case "dashboard":
        return <DashboardContent />;
      case "profile":
        return <AdminProfile />;
      case "media":
        return <MediaPage />;
      case "users":
        return <UsersContent />;
      case "newsletter":
        return <NewsLetterPage />;
      case "settings":
        return <SuperadminSettings />;
      case "blogs":
        return <BlogContent />;
      case "banner":
        return <BannerAds />;
      case "myfollowing": // ✅ Added MyFollowing in switch case
        return <MyFollowing />;
      case "ebooks":
          return <EbooksContent />;
      case "series":
          return <SeriesAdmin />;
      case "subscription":
        return <SubscriptionAdmin />;
      case "authors":
        return <AuthorsDashboard />;
      default:
        return <DashboardContent />;
    }
  };

  const navItems = [
    { id: "dashboard", icon: BarChart, label: "Dashboard" },
    ...(user?.role === "super_admin"
      ? [
          { id: "profile", icon: User, label: "Profile" },
          { id: "media", icon: ImagePlay, label: "All Media" },
          { id: "users", icon: Users, label: "Users" },
          { id: "newsletter", icon: MailCheck, label: "News Letter" },
          { id: "settings", icon: Settings, label: "Settings" },
          { id: "banner", icon: Megaphone, label: "Banner Ads" },
          { id: "myfollowing", icon: UsersRound, label: "My Followers" },
          { id: "subscription", icon: Crown, label: "Subscription" }, // <-- Add subscription tab
          { id: "authors", icon: UserCheck, label: "Authors" }, // <-- Add authors tab
          // ✅ Added "My Following"
        ]
      : [
          settingsData?.showUserProfile && {
            id: "profile",
            icon: User,
            label: "Profile",
          },
          { id: "series", icon: Library, label: "Ebook Series" },
          { id: "ebooks", icon: Book, label: "Ebooks" },

          { id: "myfollowing", icon: UsersRound, label: "My Followers" },

        ].filter(Boolean)), // Filter out false values
  ];

  return (
    <div className={styles.container}>
      <div
        className={styles.overlay}
        onClick={toggleSidebar}
        style={{ display: isSidebarOpen ? "block" : "none" }}
      />

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${isSidebarOpen ? styles.open : ""}`}
      >
        <div className={styles.sidebarHeader}>
          <h1 className={styles.logo}>Suno Kahaniyan</h1>
          <button className={styles.menuButton} onClick={toggleSidebar}>
            <X size={24} />
          </button>
        </div>

        {/* Wrap the navigation list with Scrollbars */}
        <Scrollbars autoHide style={{ flex: 1,}}>
          <nav className={styles.nav}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setCurrentSection(item.id);
                  if (window.innerWidth <= 768) {
                    setIsSidebarOpen(false);
                  }
                }}
                className={`${styles.navButton} ${
                  currentSection === item.id ? styles.active : ""
                }`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
            <button 
              className={`${styles.navButton} ${styles.logout}`}
              onClick={handleSignOut}
            >
              {isLoading ? (
                <>
                  <Loader size={20} className={styles.loadingIcon} />
                  <span>Signing out...</span>
                </>
              ) : (
                <>
                  <LogOut size={20} />
                  <span>Logout</span>
                </>
              )}
            </button>
          </nav>
        </Scrollbars>
      </aside>

      {/* Main Content */}
      <main className={styles.main}>
        <div className={styles.mobileHeader}>
          <button className={styles.menuButton} onClick={toggleSidebar}>
            <Menu size={24} />
          </button>
          <h1 className={styles.mobileLogo}>Suno Kahaniyan</h1>
        </div>

        <div className={styles.header}>
          <h2>
            {navItems.find((item) => item.id === currentSection)?.label ||
              "Dashboard"}
          </h2>
        </div>

        {renderContent()}
      </main>
    </div>
  );
}

export default Dashboard;